import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import { isNotFoundError } from '@/lib/utils/is-not-found-error';
import {
  computingRetryDelayMs,
  isComputingError,
  shouldRetryComputing,
} from '@/lib/utils/is-computing-error';

/** TanStack's own default wait: 1s, 2s, 4s … capped at 30s. */
const defaultRetryDelay = (failureCount: number): number =>
  Math.min(1000 * 2 ** failureCount, 30_000);

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (query.state.data !== undefined) {
        // Error already surfaced via authenticatedRequest's showNotification
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: (_error) => {
      // Error already surfaced via authenticatedRequest's showNotification
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      retry: (failureCount, error: unknown) => {
        // A bad link stays bad; retrying only delays its empty state (NPJB-750).
        if (isNotFoundError(error)) return false;
        // "Still computing" (NPJB-800): the backend keeps working, so ask again for ~3 min.
        // The query stays pending/fetching between tries, so the UI keeps loading.
        if (isComputingError(error)) return shouldRetryComputing(failureCount, error);
        const err = error as Record<string, unknown>;
        const response = err?.response as Record<string, unknown> | undefined;
        const status = (err?.status ?? response?.status) as number | undefined;
        if (status && [400, 401, 403, 422].includes(status)) return false;
        return failureCount < 2;
      },
      retryDelay: (failureCount, error: unknown) =>
        isComputingError(error) ? computingRetryDelayMs(error) : defaultRetryDelay(failureCount),
    },
    mutations: {
      retry: false,
    },
  },
});
